import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import { Page, Card, SkeletonBodyText, Tabs } from '@shopify/polaris';
import { getRequest, putRequest } from '../../shared/components/functions';
import InteractionForm from '../interactions/components/InteractionForm';
import InteractionDocuments from './components/InteractionDocuments';

export default function InteractionPage() {
  const { id } = useParams();
  const pagination = useRecordPagination('interactions', id, 'interaction');
  const navigate = useNavigate();
  const [interaction, setInteraction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);

  useEffect(() => {
    if (id && id !== 'new') {
      fetchInteraction();
    } else {
      setIsLoading(false);
    }
  }, [id]);

  const fetchInteraction = async () => {
    try {
      setIsLoading(true);
      const data = await getRequest(`/api/interactions/${id}`);
      if (data && typeof data === 'object') {
        // Ensure we have a valid interaction object with at least an id
        if (data.id || data.ID) {
          setInteraction(data);
        } else {
          console.error('Invalid interaction data:', data);
        }
      } else {
        console.error('No data returned from API');
      }
    } catch (error) {
      console.error('Error fetching interaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async (updatedData) => {
    try {
      await putRequest(`/api/interactions/${id}`, updatedData);
      await fetchInteraction();
      navigate('/interactions');
    } catch (error) {
      console.error('Error updating interaction:', error);
    }
  };

  const handleTabChange = useCallback((selectedTabIndex) => {
    setSelectedTab(selectedTabIndex);
  }, []);

  if (isLoading) {
    return (
      <Page pagination={pagination} title="Interaction Details">
        <SkeletonBodyText lines={6} />
      </Page>
    );
  }

  if (!interaction && id !== 'new') {
    return (
      <Page pagination={pagination} title="Interaction Details" backAction={{ content: 'Back to Interactions', onAction: () => navigate('/interactions') }}>
        <Card>
          <p>Interaction not found</p>
        </Card>
      </Page>
    );
  }

  const tabs = [
    {
      id: 'details',
      content: 'Details',
      panelID: 'details-panel'
    },
    {
      id: 'documents',
      content: 'Documents',
      panelID: 'documents-panel'
    }
  ];

  return (
    <Page
      pagination={pagination}
      title={interaction ? `Edit interaction #${interaction.id}` : 'New Interaction'}
      backAction={{ content: 'Back to Interactions', onAction: () => navigate('/interactions') }}
    >
      <Card>
        <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
          {selectedTab === 0 && (
            <div id="details-panel" role="tabpanel">
              <InteractionForm interaction={interaction} onSubmit={handleUpdate} isNew={id === 'new'} />
            </div>
          )}
          {selectedTab === 1 && interaction?.id && (
            <div id="documents-panel" role="tabpanel">
              <InteractionDocuments interactionId={interaction.id} />
            </div>
          )}
          {selectedTab === 1 && !interaction?.id && (
            <div id="documents-panel" role="tabpanel">
              <Card>
                <p>Please save the interaction first before uploading documents.</p>
              </Card>
            </div>
          )}
        </Tabs>
      </Card>
    </Page>
  );
}
